import React, { useEffect, useState } from "react";
import {
  Button,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import {
  validateVendorTypeField,
  validationMessages,
  MIN_TEXT_FIELD_LENGTH,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const VendorTypes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [vendorTypeInput, setVendorTypeInput] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [vendorTypeList, setVendorTypeList] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [vendorTypeError, setVendorTypeError] = useState<boolean>(false);
  const [vendorTypeErrorMessage, setVendorTypeErrorMessage] =
    useState<string>("");

  useEffect(() => {
    if (fieldValues) {
      setVendorTypeList(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(vendorTypeList);
    editClicked &&
      setSelectedId(checkData(vendorTypeList, selectedId) ? selectedId : null);
  }, [vendorTypeList]);

  const payload = {
    "Vendor Type": vendorTypeInput,
  };

  const validateVendorTypeInput = (): boolean => {
    if (!vendorTypeInput || vendorTypeInput.trim().length === 0) {
      setAlertMessage("Vendor type is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    setVendorTypeError(false);
    setVendorTypeErrorMessage("");

    if (vendorTypeInput.trim().length < MIN_TEXT_FIELD_LENGTH) {
      setVendorTypeError(true);
      setVendorTypeErrorMessage(validationMessages.vendorTypeMinLength);
      return false;
    }

    if (!validateVendorTypeField(vendorTypeInput)) {
      setVendorTypeError(true);
      setVendorTypeErrorMessage(validationMessages.vendorType);
      return false;
    }

    return true;
  };

  const updateGolfVendorTypes = () => {
    if (!validateVendorTypeInput()) {
      return;
    }
    if (
      checkDuplicate(
        vendorTypeList.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.VENDORTYPE,
        vendorTypeInput,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setVendorTypeList((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editGolfVendorTypes(null);
    setAlertMessage(`${GolfPropertyEnum.vendorTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfVendorTypes = () => {
    if (!validateVendorTypeInput()) {
      return;
    }
    if (
      checkDuplicate(vendorTypeList, DNDFieldsEnum.VENDORTYPE, vendorTypeInput)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setVendorTypeList((prev: any[]) =>
      updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord({ ...payload });
    editGolfVendorTypes(null);
  };

  const editGolfVendorTypes = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setVendorTypeInput(list?.[DNDFieldsEnum.VENDORTYPE] ?? null);
    setVendorTypeError(false);
    setVendorTypeErrorMessage("");
  };

  const deleteGolfVendorTypes = (id: number) => {
    setVendorTypeList(deleteItem(vendorTypeList, id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Vendor Types
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.vendorTypeHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfVendorTypes(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGolfVendorTypes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Vendor Types
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.vendorTypes)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="VendorTypes"
                variant="outlined"
                placeholder="Vendor Type"
                value={vendorTypeInput ?? ""}
                error={vendorTypeError}
                helperText={vendorTypeError ? vendorTypeErrorMessage : ""}
                sx={{ width: "100%" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setVendorTypeInput(e.target.value);
                  setVendorTypeError(false);
                  setVendorTypeErrorMessage("");
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateGolfVendorTypes() : addGolfVendorTypes()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Vendor Type
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Vendor Types Available</Typography>
            <Stack direction="column" gap="10px">
              {vendorTypeList ? (
                <DraggableItemList
                  itemData={vendorTypeList}
                  onOrderUpdate={(order) => setVendorTypeList(order)}
                  onEdit={(info) => editGolfVendorTypes(info)}
                  onDelete={(id) => deleteGolfVendorTypes(id)}
                  code={DNDFieldsEnum.VENDORTYPE}
                  name={DNDFieldsEnum.NONE}
                />
              ) : (
                <Typography variant="body2">
                  No Vendor Types Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default VendorTypes;
